import { createContext, useContext, useReducer, useCallback, useRef, useEffect, type ReactNode, type Dispatch } from 'react';
import type { AppState, AppAction } from '../types';
import { getSessionId } from '../utils/sessionId';
import { updateJourneyStep, saveMessage } from '../services/firestore/journeyService';
import { loadJourneyStepLabels } from '../services/firestore/journeyConfigService';
import { getAuthUid } from '../services/firebase';

// CLINICAL SAFETY: Force mockMode off on production hostnames,
// even if VITE_ENABLE_MOCK was accidentally set during build.
// Uses pattern matching to catch preview channels and custom domains.
function isProductionHostname(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  if (host === 'hemologix.com' || host === 'www.hemologix.com') return true;
  if (host.endsWith('.web.app') || host.endsWith('.firebaseapp.com')) return true;
  return false;
}

function resolveMockMode(): boolean {
  const envFlag = import.meta.env.VITE_ENABLE_MOCK === 'true';
  if (envFlag && isProductionHostname()) {
    console.error('[CLINICAL SAFETY] VITE_ENABLE_MOCK=true on production hostname. Forcing mockMode=false.');
    return false;
  }
  return envFlag;
}

const initialState: AppState = {
  screen: 'landing',
  user: null,
  labReport: null,
  protocol: null,
  chatHistory: [],
  tierAssessment: null,
  archetype: null,
  formulary: [],
  usage: null,
  isProcessing: false,
  isAuthenticated: false,
  error: null,
  mockMode: resolveMockMode(),
  triageData: null,
  analysisStatus: 'idle',
  analysisError: null,
  fullReport: null,
  checkoutEmail: null,
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_SCREEN':
      return { ...state, screen: action.payload };
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'SET_LAB_REPORT':
      return { ...state, labReport: action.payload };
    case 'SET_PROTOCOL': {
      // CLINICAL SAFETY: Reject mock protocol data on production hostnames.
      // Parallels the isMockData check in ProcessingScreen for lab reports.
      if (action.payload.isMockData && isProductionHostname()) {
        console.error('[CLINICAL SAFETY] Mock protocol rejected on production hostname.');
        return { ...state, error: 'A system error occurred. Your protocol could not be verified. Please try again or contact support.' };
      }
      return { ...state, protocol: action.payload };
    }
    case 'ADD_CHAT_MESSAGE':
      return { ...state, chatHistory: [...state.chatHistory, action.payload] };
    case 'SET_TIER_ASSESSMENT':
      return { ...state, tierAssessment: action.payload };
    case 'SET_ARCHETYPE':
      return { ...state, archetype: action.payload };
    case 'SET_FORMULARY':
      return { ...state, formulary: action.payload };
    case 'SET_USAGE':
      return { ...state, usage: action.payload };
    case 'SET_PROCESSING':
      return { ...state, isProcessing: action.payload };
    case 'SET_AUTH':
      return { ...state, isAuthenticated: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_TRIAGE_DATA':
      return { ...state, triageData: action.payload };
    case 'SET_ANALYSIS_STATUS':
      return { ...state, analysisStatus: action.payload };
    case 'SET_ANALYSIS_ERROR':
      return { ...state, analysisError: action.payload };
    case 'SET_FULL_REPORT': {
      // CLINICAL SAFETY: Reject mock report data on production hostnames.
      // Parallels the isMockData check in SET_PROTOCOL.
      if (action.payload.isMockData && isProductionHostname()) {
        console.error('[CLINICAL SAFETY] Mock full report rejected on production hostname.');
        return { ...state, error: 'A system error occurred. Your report could not be verified. Please try again or contact support.' };
      }
      return { ...state, fullReport: action.payload };
    }
    case 'SET_CHECKOUT_EMAIL':
      return { ...state, checkoutEmail: action.payload };
    case 'HYDRATE':
      return {
        ...state,
        screen: action.payload.screen,
        triageData: action.payload.triageData,
        chatHistory: action.payload.chatHistory,
      };
    case 'RESET_SESSION':
      return {
        ...initialState,
        user: state.user,
        formulary: state.formulary,
        usage: state.usage,
        isAuthenticated: state.isAuthenticated,
        mockMode: state.mockMode,
      };
    default:
      return state;
  }
}

// ── Persistence: fire-and-forget writes to journey service ──

function persistSideEffect(action: AppAction, currentState: AppState): void {
  const sessionId = getSessionId();
  // Don't persist ephemeral sessions (localStorage unavailable)
  if (sessionId.startsWith('ephemeral_')) return;
  // Don't persist if no triage data yet (journey not created)
  if (!currentState.triageData) return;

  // Screen changes, lab report, protocol → update journey step
  if (action.type === 'SET_SCREEN') {
    updateJourneyStep(sessionId, action.payload).catch((err) =>
      console.warn('[Journey] Step update failed:', err),
    );
  }

  if (action.type === 'SET_LAB_REPORT') {
    updateJourneyStep(sessionId, currentState.screen, { labReportId: action.payload.id }).catch((err) =>
      console.warn('[Journey] Lab report update failed:', err),
    );
  }

  if (action.type === 'SET_PROTOCOL') {
    updateJourneyStep(sessionId, currentState.screen, { protocolId: action.payload.id }).catch((err) =>
      console.warn('[Journey] Protocol update failed:', err),
    );
  }

  // Chat messages → save to subcollection
  if (action.type === 'ADD_CHAT_MESSAGE') {
    saveMessage(sessionId, action.payload).catch((err) =>
      console.warn('[Journey] Message save failed:', err),
    );
  }
}

// ── Context ──

interface AppContextValue {
  state: AppState;
  dispatch: Dispatch<AppAction>;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, baseDispatch] = useReducer(appReducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  // Load configurable journey step labels from Firestore on mount
  useEffect(() => {
    // Ensure anonymous auth is active before any Firestore calls
    getAuthUid().then(() => {
      loadJourneyStepLabels().catch((err) =>
        console.warn('[Journey] Step label load failed:', err),
      );
    }).catch(err => {
      console.error('[App] Auth initialization failed:', err);
    });
  }, []);

  // Wrap dispatch to add persistence side effects (fire-and-forget)
  const dispatch = useCallback<Dispatch<AppAction>>((action) => {
    baseDispatch(action);
    persistSideEffect(action, stateRef.current);
  }, []);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
